import type { EacPrecipitationSeason, MainRainySeason, PrecipitationSeason } from "./scoring/types";

/**
 * Changing Precipitation's season labels, shared by the results route and the
 * client so both sides agree on which seasons exist and how they read.
 *
 * Two shapes of site (team decisions 28 Oct 2026):
 *  - East Africa (KEN/UGA/TZA/RWA/BDI): two rows per horizon/scenario, MAM and
 *    OND, scored separately.
 *  - Malawi: ONE row per horizon/scenario, the site's main rainy season (its
 *    single wettest 3-month window): DJF, JFM or FMA.
 * Seasons are never combined into one score.
 */

/** East Africa's two seasons, in display order. Also the fallback season set
 * for an asset whose site has no precipitation rows (e.g. no coordinates). */
export const EAC_PRECIPITATION_SEASONS: readonly EacPrecipitationSeason[] = ["MAM", "OND"];

export const MAIN_RAINY_SEASONS: readonly MainRainySeason[] = ["DJF", "JFM", "FMA"];

/** Every season label the app knows, in display order. */
export const PRECIPITATION_SEASON_ORDER: readonly PrecipitationSeason[] = [
  ...EAC_PRECIPITATION_SEASONS,
  ...MAIN_RAINY_SEASONS,
];

/** Month span of each main-rainy-season window (en dash, as shown in the UI). */
export const MAIN_RAINY_SEASON_MONTHS: Record<MainRainySeason, string> = {
  DJF: "Dec–Feb",
  JFM: "Jan–Mar",
  FMA: "Feb–Apr",
};

export function isMainRainySeason(season: string | undefined | null): season is MainRainySeason {
  return season !== undefined && season !== null && (MAIN_RAINY_SEASONS as readonly string[]).includes(season);
}

export function isPrecipitationSeason(season: string | undefined | null): season is PrecipitationSeason {
  return season !== undefined && season !== null && (PRECIPITATION_SEASON_ORDER as readonly string[]).includes(season);
}

/** "main rainy season, Dec–Feb" for a main season; the code itself (MAM/OND) otherwise. */
export function precipitationSeasonDescription(season: PrecipitationSeason): string {
  return isMainRainySeason(season) ? `main rainy season, ${MAIN_RAINY_SEASON_MONTHS[season]}` : season;
}

/**
 * The seasons a site has rows for, in display order: MAM + OND for an East
 * African site, a single main season for a Malawi site. Unknown labels are
 * ignored. No rows at all falls back to MAM + OND, so an asset without a
 * lookup keeps today's two "pending" season rows.
 */
export function precipitationSeasonsForRows(rows: readonly { season: string }[]): PrecipitationSeason[] {
  const present = new Set(rows.map((r) => r.season));
  const seasons = PRECIPITATION_SEASON_ORDER.filter((s) => present.has(s));
  return seasons.length ? seasons : [...EAC_PRECIPITATION_SEASONS];
}
